import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import MicroModal from "micromodal";
import { useLocalStorage } from "../hooks/useLocalStorage";

import HorizontalStackedChart from "../HorizontalStackedChart";
import Loading from "../../Loading";
import Typography from "../../Typography";
import Paperplane from "../../Loading/Paperplane";
import StackedBarChart from "../Comparisons/StackedBarChart/StackedBarChart";
import SimpleBarChart from "../Comparisons/SimpleBarChart/SimpleBarChart";
import StackedBarChartSearch from "../Comparisons/StackedBarChartSearch/StackedBarChartSearch";
import MetaValueModalV2 from "../../modals/MetaValueModalV2";
import TextSmall from "../../manager-section/TextComponent/TextSmall";

import { ReactComponent as GoalIcon } from "../../../build/images/iconmeta.svg";
import { ReactComponent as EditMetaIcon } from "../../../build/images/editmeta.svg";

import "./indicators.css";
import axios from "axios";
import {
  getComparisonsNPSCall,
  getComparisonsNpsXServicesCall,
  getComparisonsProfileCall,
  getEngagementCall,
  getGradeAverageCall,
  getSearchSendedCall,
  indicatorsGeneralPdfCall,
} from "../../../actions";
import ColoredBarChart from "../ColoredBarChart";
import { convertJsonToObject } from "../../../utils/convertJsonToObject";
import LegendOfGraphic from "../LegendOfGraphic";

export default function Indicators() {
  const agentDashboardFilter = useSelector(
    (state) => state.agentDashboardFilter
  );
  const cardsOfCallCenterIndicators = useSelector(
    (state) => state.cardsOfCallCenterIndicators
  );
  const [dataIndicators, setDataIndicators] = useState([]);
  const [isLoadingDataIndicators, setIsLoadingDataIndicators] = useState(false);
  const [value, setValue] = useState(getMetaValue());
  const [metaValue, setMetaValue] = useState(getMetaValue());
  const [loadingNPSGraphic, setLoadingNPSGraphic] = useState(false);
  const [dataToNPSGraphic, setDataToNPSGraphic] = useState(null);
  const [currentYear, setCurrentYear] = useState(
    getYear(
      "current",
      agentDashboardFilter.initialDateYear,
      agentDashboardFilter.finalDateYear
    )
  );
  const [lastYear, setLastYear] = useState(
    getYear(
      "last",
      agentDashboardFilter.initialDateYear,
      agentDashboardFilter.finalDateYear
    )
  );
  const [loadingGradeAverageGraphic, setLoadingGradeAverageGraphic] =
    useState(false);
  const [dataToGradeAverageGraphic, setDataToGradeAverageGraphic] =
    useState(null);
  const [loadingEngagementGraphic, setLoadingEngagementGraphic] =
    useState(false);
  const [dataToEngagementGraphic, setDataToEngagementGraphic] = useState(null);
  const [loadingProfileGraphic, setLoadingProfileGraphic] = useState(false);
  const [dataToProfileGraphic, setDataToProfileGraphic] = useState(null);
  const [profile, setProfile] = useState(
    agentDashboardFilter.profileSelectedAgenteScreen
  );
  const [loadingSearchSendedGraphic, setLoadingSearchSendedGraphic] =
    useState(false);
  const [dataToSearchSendedGraphic, setDataToSearchSendedGraphic] =
    useState(null);
  const [loadingNPSXServicesGraphic, setLoadingNPSXServicesGraphic] =
    useState(false);
  const [dataToNPSXServicesGraphic, setDataToNPSXServicesGraphic] =
    useState(null);
  const [NPSError, setNPSError] = useState(false);
  const [gradeAverageError, setGradeAverageError] = useState(false);
  const [engagementError, setEngagementError] = useState(false);
  const [profileError, setProfileError] = useState(false);
  const [searchSendedError, setSearchSendedError] = useState(false);
  const [NPSXServicesError, setNPSXServicesError] = useState(false);
  const hasAnyError =
    NPSError ||
    gradeAverageError ||
    engagementError ||
    profileError ||
    searchSendedError ||
    NPSXServicesError;

  const filteredDataToNPSGraphic = useMemo(() => {
    const hasDataToNPSGraphic = !!dataToNPSGraphic;
    if (hasDataToNPSGraphic) {
      const isEmpty = dataToNPSGraphic.length === 0;
      if (!isEmpty) {
        const updatedData = dataToNPSGraphic.map((datum) => {
          datum.goal = metaValue;
          return datum;
        });

        return updatedData;
      } else {
        return [];
      }
    } else {
      return null;
    }
  }, [dataToNPSGraphic, metaValue]);

  async function updateNPSXServicesGraphic(
    yearStart,
    yearEnd,
    businessId = "",
    unityId = "",
    profile = "",
    source
  ) {
    let dataToRender = [];
    setNPSXServicesError(false);
    setLoadingNPSXServicesGraphic(true);
    const response = await getComparisonsNpsXServicesCall(
      yearStart,
      yearEnd,
      businessId,
      unityId,
      profile,
      source
    );
    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const data = response.data;
      const hasData = !!data && data.length > 0;
      if (hasData) {
        function putDataInMonth(rawData, goal = 90) {
          let graphicData = [
            {
              name: "JAN",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "FEV",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "MAR",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "ABR",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "MAI",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "JUN",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "JUL",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "AGO",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "SET",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "OUT",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "NOV",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "DEZ",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
          ];

          rawData.forEach((data) => {
            const date = data.data_nps.split("-");
            const result = Number(data.qtde)
              ? Number(data.qtde)
              : 0
              ? Number(data.qtde_procedimentos)
              : 0;
            const monthNumber = date && date.length ? date[1] : "01";
            const yearNumber = date && date.length ? date[0] : "2024";
            let isCurrentYear = false;
            if (yearNumber === currentYear) {
              isCurrentYear = true;
            }
            const indexOfMonth = getMonthIndex(monthNumber);
            if (isCurrentYear) {
              graphicData[indexOfMonth].currentYearData =
                graphicData[indexOfMonth].currentYearData + result;
            } else {
              graphicData[indexOfMonth].lastYearData =
                graphicData[indexOfMonth].lastYearData + result;
            }
          });
          return graphicData;
        }

        dataToRender = putDataInMonth(data, 93);
      }
      setDataToNPSXServicesGraphic(dataToRender);
      setLoadingNPSXServicesGraphic(false);
    } else {
      setDataToNPSXServicesGraphic(dataToRender);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setNPSXServicesError(true);
          setLoadingNPSXServicesGraphic(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }

    return dataToRender;
  }

  async function updateSearchSendedGraphic(
    yearStart,
    yearEnd,
    channelId = "",
    avaliationId = "",
    source
  ) {
    let dataToRender = [];
    setSearchSendedError(false);
    setLoadingSearchSendedGraphic(true);
    const response = await getSearchSendedCall(
      yearStart,
      yearEnd,
      channelId,
      avaliationId,
      source
    );

    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const data = response.data;
      const hasData = !!data && data.length > 0;
      if (hasData) {
        function putDataInMonth(rawData) {
          let graphicData = [
            {
              name: "JAN",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "FEV",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "MAR",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "ABR",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "MAI",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "JUN",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "JUL",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "AGO",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "SET",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "OUT",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "NOV",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
            {
              name: "DEZ",
              currentYearSended: 0,
              currentYearAnswered: 0,
              lastYearSended: 0,
              lastYearAnswered: 0,
            },
          ];

          rawData.forEach((data) => {
            const date = data.data_nps.split("-");
            const sended = data.enviadas ? Number(data.enviadas) : 0;
            const answered = data.respondidas ? Number(data.respondidas) : 0;
            const monthNumber = date && date.length ? date[1] : "01";
            const yearNumber = date && date.length ? date[0] : "2024";
            let isCurrentYear = false;
            if (yearNumber === currentYear) {
              isCurrentYear = true;
            }
            const indexOfMonth = getMonthIndex(monthNumber);
            if (isCurrentYear) {
              graphicData[indexOfMonth].currentYearSended =
                graphicData[indexOfMonth].currentYearSended + sended - answered;

              graphicData[indexOfMonth].currentYearAnswered =
                graphicData[indexOfMonth].currentYearAnswered + answered;
            } else {
              graphicData[indexOfMonth].lastYearSended =
                graphicData[indexOfMonth].lastYearSended + sended - answered;

              graphicData[indexOfMonth].lastYearAnswered =
                graphicData[indexOfMonth].lastYearAnswered + answered;
            }
          });
          return graphicData;
        }

        dataToRender = putDataInMonth(data, 93);
      }

      setDataToSearchSendedGraphic(dataToRender);
      setLoadingSearchSendedGraphic(false);
    } else {
      setDataToSearchSendedGraphic(dataToRender);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setSearchSendedError(true);
          setLoadingSearchSendedGraphic(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }

    return dataToRender;
  }

  async function updateProfileGraphic(
    yearStart,
    yearEnd,
    channelId = "",
    avaliationId = "",
    profile = "",
    source
  ) {
    let dataToRender = [];
    setProfileError(false);
    setLoadingProfileGraphic(true);
    const response = await getComparisonsProfileCall(
      yearStart,
      yearEnd,
      channelId,
      avaliationId,
      profile,
      source
    );

    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const data = response.data;
      const hasData = !!data && data.length > 0;
      if (hasData) {
        function putDataInMonth(rawData, goal = 90) {
          let graphicData = [
            {
              name: "JAN",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "FEV",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "MAR",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "ABR",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "MAI",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "JUN",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "JUL",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "AGO",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "SET",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "OUT",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "NOV",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "DEZ",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
          ];

          rawData.forEach((data) => {
            const date = data.data_nps.split("-");
            const result = data.resultado ? Number(data.resultado) : 0;
            const monthNumber = date && date.length ? date[1] : "01";
            const yearNumber = date && date.length ? date[0] : "2024";
            let isCurrentYear = false;
            if (yearNumber === currentYear) {
              isCurrentYear = true;
            }
            const indexOfMonth = getMonthIndex(monthNumber);
            if (isCurrentYear) {
              graphicData[indexOfMonth].currentYearData =
                graphicData[indexOfMonth].currentYearData + result;
            } else {
              graphicData[indexOfMonth].lastYearData =
                graphicData[indexOfMonth].lastYearData + result;
            }
          });
          return graphicData;
        }

        dataToRender = putDataInMonth(data, 93);
      }

      setDataToProfileGraphic(dataToRender);
      setLoadingProfileGraphic(false);
    } else {
      setDataToProfileGraphic(dataToRender);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setProfileError(true);
          setLoadingProfileGraphic(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }

    return dataToRender;
  }

  async function updateEngagementGraphic(
    yearStart,
    yearEnd,
    channelId = "",
    avaliationId = "",
    source
  ) {
    let updatedDataToEngagementGraphic = [];
    setEngagementError(false);
    setLoadingEngagementGraphic(true);
    const response = await getEngagementCall(
      yearStart,
      yearEnd,
      channelId,
      avaliationId,
      source
    );
    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const data = response.data;
      const hasData = !!data && data.length > 0;
      if (hasData) {
        function putDataInMonth(rawData) {
          let graphicData = [
            {
              name: "JAN",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "FEV",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "MAR",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "ABR",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "MAI",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "JUN",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "JUL",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "AGO",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "SET",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "OUT",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "NOV",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
            {
              name: "DEZ",
              currentYearDetractor: 0,
              currentYearNeutral: 0,
              currentYearPromoter: 0,
              lastYearDetractor: 0,
              lastYearNeutral: 0,
              lastYearPromoter: 0,
            },
          ];

          rawData.forEach((data) => {
            const date = data.data_nps.split("-");
            const result = data.qtde ? Number(data.qtde) : 0;
            const monthNumber = date && date.length ? date[1] : "01";
            const yearNumber = date && date.length ? date[0] : "2024";
            let isCurrentYear = false;
            if (yearNumber === currentYear) {
              isCurrentYear = true;
            }
            const indexOfMonth = getMonthIndex(monthNumber);
            if (isCurrentYear) {
              if (data.tipo === "detrator" || data.tipo === "Detrator") {
                graphicData[indexOfMonth].currentYearDetractor =
                  graphicData[indexOfMonth].currentYearDetractor + result;
              } else if (data.tipo === "neutro" || data.tipo === "Neutro") {
                graphicData[indexOfMonth].currentYearNeutral =
                  graphicData[indexOfMonth].currentYearNeutral + result;
              } else if (data.tipo === "promotor" || data.tipo === "Promotor") {
                graphicData[indexOfMonth].currentYearPromoter =
                  graphicData[indexOfMonth].currentYearPromoter + result;
              }
            } else {
              if (data.tipo === "detrator" || data.tipo === "Detrator") {
                graphicData[indexOfMonth].lastYearDetractor =
                  graphicData[indexOfMonth].lastYearDetractor + result;
              } else if (data.tipo === "neutro" || data.tipo === "Neutro") {
                graphicData[indexOfMonth].lastYearNeutral =
                  graphicData[indexOfMonth].lastYearNeutral + result;
              } else if (data.tipo === "promotor" || data.tipo === "Promotor") {
                graphicData[indexOfMonth].lastYearPromoter =
                  graphicData[indexOfMonth].lastYearPromoter + result;
              }
            }
          });
          return graphicData;
        }

        const aux = putDataInMonth(data);
        updatedDataToEngagementGraphic = aux.map((data) => {
          const totalCurrentYear =
            data.currentYearDetractor +
            data.currentYearNeutral +
            data.currentYearPromoter;
          const totalLastYear =
            data.lastYearDetractor +
            data.lastYearNeutral +
            data.lastYearPromoter;
          const currentDetractorPercent = totalCurrentYear
            ? Number(data.currentYearDetractor / totalCurrentYear)
            : 0;
          const currentNeutralPercent = totalCurrentYear
            ? Number(data.currentYearNeutral / totalCurrentYear)
            : 0;
          const currentPromoterPercent = totalCurrentYear
            ? Number(data.currentYearPromoter / totalCurrentYear)
            : 0;
          const lastDetractorPercent = totalLastYear
            ? Number(data.lastYearDetractor / totalLastYear)
            : 0;
          const lastNeutralPercent = totalLastYear
            ? Number(data.lastYearNeutral / totalLastYear)
            : 0;
          const lastPromoterPercent = totalLastYear
            ? Number(data.lastYearPromoter / totalLastYear)
            : 0;
          return {
            name: data.name,
            currentYearDetractor: currentDetractorPercent
              ? currentDetractorPercent * 100
              : 0,
            currentYearNeutral: currentNeutralPercent
              ? currentNeutralPercent * 100
              : 0,
            currentYearPromoter: currentPromoterPercent
              ? currentPromoterPercent * 100
              : 0,
            lastYearDetractor: lastDetractorPercent
              ? lastDetractorPercent * 100
              : 0,
            lastYearNeutral: lastNeutralPercent ? lastNeutralPercent * 100 : 0,
            lastYearPromoter: lastPromoterPercent
              ? lastPromoterPercent * 100
              : 0,
          };
        });
      }

      setDataToEngagementGraphic(updatedDataToEngagementGraphic);
      setLoadingEngagementGraphic(false);
    } else {
      setDataToEngagementGraphic(updatedDataToEngagementGraphic);
      const hasError = !!response && !!response.error;
      if (hasError) {
        setEngagementError(true);
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setLoadingEngagementGraphic(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }

    return updatedDataToEngagementGraphic;
  }

  async function updateGradeAverageGraphic(
    yearStart,
    yearEnd,
    channelId = "",
    avaliattionId = "",
    source
  ) {
    let updatedDataToGradeAverageGraphic = [];
    setGradeAverageError(false);
    setLoadingGradeAverageGraphic(true);
    const response = await getGradeAverageCall(
      yearStart,
      yearEnd,
      channelId,
      avaliattionId,
      source
    );

    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const data = response.data;
      const hasData = !!data && data.length > 0;
      if (hasData) {
        function putDataInMonth(rawData, goal = 90) {
          let graphicData = [
            {
              name: "JAN",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "FEV",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "MAR",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "ABR",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "MAI",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "JUN",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "JUL",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "AGO",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "SET",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "OUT",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "NOV",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "DEZ",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
          ];

          rawData.forEach((data) => {
            const date = data.data_nps.split("-");
            const result = data.resultado ? Number(data.resultado) : 0;
            const monthNumber = date && date.length ? date[1] : "01";
            const yearNumber = date && date.length ? date[0] : "2024";
            let isCurrentYear = false;
            if (yearNumber === currentYear) {
              isCurrentYear = true;
            }
            const indexOfMonth = getMonthIndex(monthNumber);
            if (isCurrentYear) {
              graphicData[indexOfMonth].currentYearData =
                graphicData[indexOfMonth].currentYearData + result;
            } else {
              graphicData[indexOfMonth].lastYearData =
                graphicData[indexOfMonth].lastYearData + result;
            }
          });
          return graphicData;
        }

        updatedDataToGradeAverageGraphic = putDataInMonth(data, 93);
      }

      setDataToGradeAverageGraphic(updatedDataToGradeAverageGraphic);
      setLoadingGradeAverageGraphic(false);
    } else {
      setDataToGradeAverageGraphic(updatedDataToGradeAverageGraphic);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setGradeAverageError(true);
          setLoadingGradeAverageGraphic(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }
  }

  function getMonthIndex(monthNumber) {
    switch (monthNumber) {
      case "01":
        return 0;
      case "02":
        return 1;
      case "03":
        return 2;
      case "04":
        return 3;
      case "05":
        return 4;
      case "06":
        return 5;
      case "07":
        return 6;
      case "08":
        return 7;
      case "09":
        return 8;
      case "10":
        return 9;
      case "11":
        return 10;
      case "12":
        return 11;
      default:
        return 0;
    }
  }

  async function updateNPSGraphic(
    yearStart,
    yearEnd,
    channelId = "",
    avaliationId = "",
    source
  ) {
    setNPSError(false);
    setLoadingNPSGraphic(true);
    let updatedDataToNPSGraphic = [];
    const response = await getComparisonsNPSCall(
      yearStart,
      yearEnd,
      channelId,
      avaliationId,
      source
    );
    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const data = response.data;
      const hasData = !!data && data.length > 0;
      if (hasData) {
        function putDataInMonth(rawData, goal = 90) {
          let graphicData = [
            {
              name: "JAN",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "FEV",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "MAR",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "ABR",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "MAI",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "JUN",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "JUL",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "AGO",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "SET",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "OUT",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "NOV",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
            {
              name: "DEZ",
              currentYearData: 0,
              lastYearData: 0,
              goal,
            },
          ];

          rawData.forEach((data) => {
            const date = data.data_nps.split("-");
            const result = data.resultado ? Number(data.resultado) : 0;
            const monthNumber = date && date.length ? date[1] : "01";
            const yearNumber = date && date.length ? date[0] : "2024";
            let isCurrentYear = false;
            if (yearNumber === currentYear) {
              isCurrentYear = true;
            }
            const indexOfMonth = getMonthIndex(monthNumber);
            if (isCurrentYear) {
              graphicData[indexOfMonth].currentYearData =
                graphicData[indexOfMonth].currentYearData + result;
            } else {
              graphicData[indexOfMonth].lastYearData =
                graphicData[indexOfMonth].lastYearData + result;
            }
          });
          return graphicData;
        }
        updatedDataToNPSGraphic = putDataInMonth(data, metaValue);
      }

      setDataToNPSGraphic(updatedDataToNPSGraphic);
      setLoadingNPSGraphic(false);
    } else {
      setDataToNPSGraphic(updatedDataToNPSGraphic);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setNPSError(true);
          setLoadingNPSGraphic(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }

    return updatedDataToNPSGraphic;
  }

  function getYear(type, datestart, dateend) {
    const lastYear = format(new Date(datestart), "yyyy");
    const currentYear = format(new Date(dateend), "yyyy");
    const dateStartDateFormat = new Date(datestart);
    if (lastYear === currentYear) {
      let updatedLastYear = new Date(datestart);
      updatedLastYear.setFullYear(dateStartDateFormat.getFullYear() - 1);
      updatedLastYear = format(new Date(updatedLastYear), "yyyy");
      switch (type) {
        case "last":
          return updatedLastYear;
        case "current":
          return currentYear;
        default:
          return;
      }
    } else {
      switch (type) {
        case "last":
          return lastYear;
        case "current":
          return currentYear;
        default:
          return;
      }
    }
  }

  function getMetaValue() {
    const [storedValue] = useLocalStorage("ingWebNPS_goal", 0);
    let goal = 0;
    if (storedValue) {
      goal = storedValue;
    }
    return goal;
  }

  function renderIndicators(dataIndicators) {
    if (isLoadingDataIndicators) {
      return (
        <div className="container-graphics03-loading">
          <Loading.Paperplane />
        </div>
      );
    }

    const hasDataIndicators = !!dataIndicators && dataIndicators.length > 0;
    if (hasDataIndicators) {
      return (
        <section className="container-graphics03">
          {dataIndicators.map((indicatorInfo, index) => {
            const hasDataToGenerateGraphic =
              !!indicatorInfo.dataToGenerateGraphic &&
              Array.isArray(indicatorInfo.dataToGenerateGraphic) &&
              indicatorInfo.dataToGenerateGraphic.length > 0;
            return (
              <div
                key={index}
                className="container-graphics03__item"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                  padding: "12px 16px",
                }}
              >
                <div className="container-graphics03__item__header">
                  <h2>{indicatorInfo.title}</h2>
                </div>
                {hasDataToGenerateGraphic && (
                  <ColoredBarChart data={indicatorInfo.dataToGenerateGraphic} />
                )}
                {/*<HorizontalStackedChart
                  title={indicatorInfo.title}
                  subtitle={indicatorInfo.subtitle}
                  data={indicatorInfo.data}
                  subqualifiers={indicatorInfo.subqualifiers}
                  isTooltippyActive={false}
                  isScrollActive={true}
                />*/}
                <div>
                  <LegendOfGraphic data={indicatorInfo.dataToGenerateGraphic} />
                </div>
              </div>
            );
          })}
        </section>
      );
    }

    return null;
  }

  async function getDataToIndicatorsV2(
    dateStart,
    dateEnd,
    channelId,
    avaliationId,
    source
  ) {
    setIsLoadingDataIndicators(true);
    const formatedInitialDate = format(new Date(dateStart), "yyyy-MM-dd");
    const formatedFinalDate = format(new Date(dateEnd), "yyyy-MM-dd");
    let avaliationIds = "";
    const hasUnits = !!avaliationId && avaliationId.length > 0;
    if (hasUnits) {
      avaliationIds = avaliationId.map((unit) => unit.id).join(",");
    }
    let channelIds = "";
    const hasBusiness = !!channelId && channelId.length > 0;
    if (hasBusiness) {
      channelIds = channelId.map((data) => data.id).join(",");
    }

    let updatedDataIndicators = [];

    const response = await indicatorsGeneralPdfCall(
      formatedInitialDate,
      formatedFinalDate,
      channelIds,
      avaliationIds,
      source
    );

    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const rawDataIndicators = response.data;
      const hasRawDataIndicators =
        !!rawDataIndicators && rawDataIndicators.length > 0;
      if (hasRawDataIndicators) {
        updatedDataIndicators = rawDataIndicators.map((indicator) => {
          const answers = convertJsonToObject(indicator.respostas);
          let dataToGenerateGraphic = [];
          const hasAnswers =
            !!answers && Array.isArray(answers) && answers.length > 0;
          if (hasAnswers) {
            let total = answers.reduce((accumulator, current) => {
              return (accumulator += current.qtde);
            }, 0);
            dataToGenerateGraphic = answers.map((answer) => {
              const name = total
                ? `${((answer.qtde / total) * 100).toFixed(1)}%`
                : 0;
              const value = answer.qtde;
              const color = answer.color ? answer.color : "#455CC7";
              return {
                name,
                value,
                color,
                label: answer.resposta,
              };
            });
          }

          return {
            id: indicator.id_pergunta,
            title: indicator.pergunta,
            dataToGenerateGraphic,
          };
        });
      }

      setDataIndicators(updatedDataIndicators);
      setIsLoadingDataIndicators(false);
    } else {
      setDataIndicators(updatedDataIndicators);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setIsLoadingDataIndicators(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }
  }

  async function getDataToIndicators(
    dateStart,
    dateEnd,
    channelId,
    avaliationId,
    source
  ) {
    setIsLoadingDataIndicators(true);
    const formatedInitialDate = format(new Date(dateStart), "yyyy-MM-dd");
    const formatedFinalDate = format(new Date(dateEnd), "yyyy-MM-dd");
    let avaliationIds = "";
    const hasUnits = !!avaliationId && avaliationId.length > 0;
    if (hasUnits) {
      avaliationIds = avaliationId.map((unit) => unit.id).join(",");
    }
    let channelIds = "";
    const hasBusiness = !!channelId && channelId.length > 0;
    if (hasBusiness) {
      channelIds = channelId.map((data) => data.id).join(",");
    }

    let updatedDataIndicators = [];

    const response = await indicatorsGeneralPdfCall(
      formatedInitialDate,
      formatedFinalDate,
      channelIds,
      avaliationIds,
      source
    );

    const hasResponse = !!response && response.status === 200;
    if (hasResponse) {
      const rawDataIndicators = response.data;
      const hasRawDataIndicators =
        !!rawDataIndicators && rawDataIndicators.length > 0;
      if (hasRawDataIndicators) {
        updatedDataIndicators = rawDataIndicators.map((indicator) => {
          const resultNumber = Number(indicator.resultado);
          let goodPercent = 0;
          let badPercent = 0;
          const isResultNumberANumber = typeof resultNumber === "number";
          if (isResultNumberANumber) {
            goodPercent = resultNumber;
            badPercent = 100 - resultNumber;
          }

          let good = 0;
          let bad = 0;
          try {
            const asnwers = JSON.parse(indicator.respostas);
            const hasAnswers = !!asnwers && asnwers.length > 0;
            if (hasAnswers) {
              asnwers.forEach((answer) => {
                const isGood =
                  answer.resposta === "Bom" ||
                  answer.resposta === "bom" ||
                  answer.color === "green";
                if (isGood) {
                  good = good + answer.qtde;
                } else {
                  bad = bad + answer.qtde;
                }
              });
            }
          } catch (error) {
            console.log("Erro ao realizar o PARSE: ", error);
          }
          let total = good + bad;

          let subqualifiers = [];
          let rawSubqualifiers = null;
          try {
            const parsedRawSubqualifiers = JSON.parse(
              indicator.subqualificadores
            );
            rawSubqualifiers = parsedRawSubqualifiers;
          } catch (error) {
            console.log("erro ao realizar o parse: ", error);
          }
          const hasRawSubqualifiers =
            !!rawSubqualifiers && rawSubqualifiers.length > 0;
          if (hasRawSubqualifiers) {
            subqualifiers = rawSubqualifiers.map((sub, index) => {
              return {
                label: sub.subqualificador,
                value: sub.qtde,
                id: index,
              };
            });
          }

          const hasSubqualifiers = !!subqualifiers && subqualifiers.length > 0;
          if (hasSubqualifiers) {
            subqualifiers.sort((a, b) => b.value - a.value);
          }

          return {
            data: {
              good,
              bad,
              total,
              goodPercent,
              badPercent,
            },
            subqualifiers,
            title: indicator.pergunta,
            subtitle: "Subqualificadores",
          };
        });
      }

      setDataIndicators(updatedDataIndicators);
      setIsLoadingDataIndicators(false);
    } else {
      setDataIndicators(updatedDataIndicators);
      const hasError = !!response && !!response.error;
      if (hasError) {
        const isCanceled = axios.isCancel(response.error);
        if (!isCanceled) {
          setIsLoadingDataIndicators(false);
        }
      }
      console.log(
        "Falha, não foi possível carregar as informações!",
        "Verifique sua conexão e tente novamente mais tarde."
      );
    }
  }

  const handleMetaValue = () => {
    setMetaValue(value);
    //localStorage.setItem("ingWebNPS_goal", value);
    MicroModal.close("modal__edit_meta_value");
  };

  const OpenModalMeta = () => {
    setValue(metaValue);
    MicroModal.show("modal__edit_meta_value");
  };

  function renderComparations() {
    return (
      <section className="comparisons">
        <ToastContainer position="top-center" />
        <div className="comparisons__holder">
          <div className="comparasions__graphics-container">
            {cardsOfCallCenterIndicators.card2 && (
              <div className="nps-chart-card">
                <header className="nps-chart-card__header">
                  <Typography.H5 weight="700" color="gray-180">
                    NPS
                  </Typography.H5>
                  <div
                    className="manager-component__card-perfomance__title-itens"
                    onClick={() => OpenModalMeta()}
                    style={{ cursor: "pointer" }}
                  >
                    <div>
                      <GoalIcon />
                    </div>
                    <TextSmall text={`Meta de ${metaValue}%`} color="gray" />
                    <div>
                      <EditMetaIcon />
                    </div>
                  </div>
                </header>
                <div className="Surveys-sent-answered-chart-card__graphic">
                  {loadingNPSGraphic || filteredDataToNPSGraphic === null ? (
                    <div className="Surveys-sent-answered-chart-card__graphic__loading">
                      <Paperplane />
                    </div>
                  ) : (
                    <SimpleBarChart
                      labelCurrentYear="NPS"
                      labelLastYear="NPS"
                      currentYear={currentYear}
                      lastYear={lastYear}
                      isGoalActive={true}
                      dataStart={filteredDataToNPSGraphic}
                    />
                  )}
                </div>
              </div>
            )}

            {cardsOfCallCenterIndicators.card3 && (
              <div className="average-grade-chart-card">
                <header className="average-grade-chart-card__header">
                  <Typography.H5 weight="700" color="gray-180">
                    Média de notas
                  </Typography.H5>
                  {/*<HamburgerIcon />*/}
                </header>
                <div className="Surveys-sent-answered-chart-card__graphic">
                  {loadingGradeAverageGraphic ||
                  dataToGradeAverageGraphic === null ? (
                    <div className="Surveys-sent-answered-chart-card__graphic__loading">
                      <Paperplane />
                    </div>
                  ) : (
                    <SimpleBarChart
                      labelCurrentYear="Média de notas"
                      currentYear={currentYear}
                      lastYear={lastYear}
                      labelLastYear="Média de notas"
                      dataStart={dataToGradeAverageGraphic}
                      isLimitDomainActive={true}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="comparasions__graphics-container">
            {cardsOfCallCenterIndicators.card4 && (
              <div className="engagement-chart-card">
                <header className="engagement-chart-card__header">
                  <Typography.H5 weight="700" color="gray-180">
                    {"Engajamento (%)"}
                  </Typography.H5>
                  {/*<HamburgerIcon />*/}
                </header>
                <div className="engagement-chart-card__graphic">
                  {loadingEngagementGraphic ||
                  dataToEngagementGraphic === null ? (
                    <div className="Surveys-sent-answered-chart-card__graphic__loading">
                      <Paperplane />
                    </div>
                  ) : (
                    <StackedBarChart
                      dataStart={dataToEngagementGraphic}
                      currentYear={currentYear}
                      lastYear={lastYear}
                    />
                  )}
                </div>
              </div>
            )}

            {cardsOfCallCenterIndicators.card5 && (
              <div className="profile-chart-card">
                <header className="profile-chart-card__header">
                  <Typography.H5 weight="700" color="gray-180">
                    Perfil
                  </Typography.H5>
                  {/*<HamburgerIcon />*/}
                </header>
                <div className="Surveys-sent-answered-chart-card__graphic">
                  {loadingProfileGraphic || dataToProfileGraphic === null ? (
                    <div className="Surveys-sent-answered-chart-card__graphic__loading">
                      <Paperplane />
                    </div>
                  ) : (
                    <SimpleBarChart
                      dataStart={dataToProfileGraphic}
                      currentYear={currentYear}
                      lastYear={lastYear}
                      labelCurrentYear={
                        profile && profile.length ? profile[0].name : "Promotor"
                      }
                      labelLastYear={
                        profile && profile.length ? profile[0].name : "Promotor"
                      }
                      isLimitDomainActive={true}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="comparasions__graphics-container">
            {cardsOfCallCenterIndicators.card6 && (
              <div className="Surveys-sent-answered-chart-card">
                <header className="Surveys-sent-answered-chart-card__header">
                  <Typography.H5 weight="700" color="gray-180">
                    {"Pesquisas enviadas / Respondidas (Quantidade)"}
                  </Typography.H5>
                  {/*<HamburgerIcon />*/}
                </header>
                <div className="Surveys-sent-answered-chart-card__graphic">
                  {loadingSearchSendedGraphic ||
                  dataToSearchSendedGraphic === null ? (
                    <div className="Surveys-sent-answered-chart-card__graphic__loading">
                      <Paperplane />
                    </div>
                  ) : (
                    <StackedBarChartSearch
                      dataStart={dataToSearchSendedGraphic}
                      currentYear={currentYear}
                      lastYear={lastYear}
                    />
                  )}
                </div>
              </div>
            )}

            {cardsOfCallCenterIndicators.card7 && (
              <div className="nps-services-chart-card">
                <header className="nps-services-chart-card__header">
                  <Typography.H5 weight="700" color="gray-180">
                    Quantidade de participantes por perfil
                  </Typography.H5>
                  {/*<HamburgerIcon />*/}
                </header>
                <div className="Surveys-sent-answered-chart-card__graphic">
                  {loadingNPSXServicesGraphic ||
                  dataToNPSXServicesGraphic === null ? (
                    <div className="Surveys-sent-answered-chart-card__graphic__loading">
                      <Paperplane />
                    </div>
                  ) : (
                    <SimpleBarChart
                      dataStart={dataToNPSXServicesGraphic}
                      currentYear={currentYear}
                      lastYear={lastYear}
                      labelCurrentYear={
                        profile && profile.length ? profile[0].name : "Promotor"
                      }
                      labelLastYear={
                        profile && profile.length ? profile[0].name : "Promotor"
                      }
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          <MetaValueModalV2
            handleMetaValue={handleMetaValue}
            setValue={setValue}
            value={value}
            setMetaValue={setMetaValue}
            storageKey="ingWebNPS_goal"
            text="Selecione a meta"
          />
        </div>
      </section>
    );
  }

  useEffect(() => {
    const sourceDataToIndicators = axios.CancelToken.source();
    const sourceNPSGraphic = axios.CancelToken.source();
    const sourceGradeAverageGraphic = axios.CancelToken.source();
    const sourceEngagementGraphic = axios.CancelToken.source();
    const sourceProfileGraphic = axios.CancelToken.source();
    const sourceSearchSendedGraphic = axios.CancelToken.source();
    const sourceNPSXServicesGraphic = axios.CancelToken.source();

    const hasChannels =
      !!agentDashboardFilter.channelSelectedAgentScreen &&
      agentDashboardFilter.channelSelectedAgentScreen.length > 0;
    const channelIds = hasChannels
      ? agentDashboardFilter.channelSelectedAgentScreen
          .map((channel) => {
            return channel.id;
          })
          .join(",")
      : "";

    const hasAvaliations =
      !!agentDashboardFilter.avaliationSelectedAgentScreen &&
      agentDashboardFilter.avaliationSelectedAgentScreen.length > 0;
    const avaliationIds = hasAvaliations
      ? agentDashboardFilter.avaliationSelectedAgentScreen
          .map((avaliation) => {
            return avaliation.id;
          })
          .join(",")
      : "";

    if (agentDashboardFilter) {
      setProfile(agentDashboardFilter.profileSelectedAgenteScreen);
      getDataToIndicatorsV2(
        agentDashboardFilter.initialDateAgenteScreen,
        agentDashboardFilter.finalDateAgentScreen,
        agentDashboardFilter.channelSelectedAgentScreen,
        agentDashboardFilter.avaliationSelectedAgentScreen,
        sourceDataToIndicators
      );

      let profileId = "PROMOTOR";
      const hasProfile =
        !!agentDashboardFilter.profileSelectedAgenteScreen &&
        agentDashboardFilter.profileSelectedAgenteScreen.length > 0;
      if (hasProfile) {
        profileId = agentDashboardFilter.profileSelectedAgenteScreen[0].id;
      }

      const formatedLastYear = format(
        new Date(agentDashboardFilter.initialDateYear),
        "yyyy"
      );
      const formatedCurrentYear = format(
        new Date(agentDashboardFilter.finalDateYear),
        "yyyy"
      );

      setLastYear(formatedLastYear);
      setCurrentYear(formatedCurrentYear);

      updateNPSGraphic(
        formatedLastYear,
        formatedCurrentYear,
        channelIds,
        avaliationIds,
        sourceNPSGraphic
      );
      updateGradeAverageGraphic(
        formatedLastYear,
        formatedCurrentYear,
        channelIds,
        avaliationIds,
        sourceGradeAverageGraphic
      );
      updateEngagementGraphic(
        formatedLastYear,
        formatedCurrentYear,
        channelIds,
        avaliationIds,
        sourceEngagementGraphic
      );
      updateProfileGraphic(
        formatedLastYear,
        formatedCurrentYear,
        channelIds,
        avaliationIds,
        profileId,
        sourceProfileGraphic
      );
      updateSearchSendedGraphic(
        formatedLastYear,
        formatedCurrentYear,
        channelIds,
        avaliationIds,
        sourceSearchSendedGraphic
      );
      updateNPSXServicesGraphic(
        formatedLastYear,
        formatedCurrentYear,
        channelIds,
        avaliationIds,
        profileId,
        sourceNPSXServicesGraphic
      );
    }

    return () => {
      sourceNPSGraphic.cancel(
        "Operation canceled by the user. (sourceNPSGraphic)"
      );
      sourceGradeAverageGraphic.cancel(
        "Operation canceled by the user. (sourceGradeAverageGraphic)"
      );
      sourceEngagementGraphic.cancel(
        "Operation canceled by the user. (sourceEngagementGraphic)"
      );
      sourceProfileGraphic.cancel(
        "Operation canceled by the user. (sourceProfileGraphic)"
      );
      sourceSearchSendedGraphic.cancel(
        "Operation canceled by the user. (sourceSearchSendedGraphic)"
      );
      sourceNPSXServicesGraphic.cancel(
        "Operation canceled by the user. (sourceNPSXServicesGraphic)"
      );
      sourceDataToIndicators.cancel(
        "Operation canceled by the user. (sourceDataToIndicators)"
      );
    };
  }, [agentDashboardFilter]);

  useEffect(() => {
    if (hasAnyError) {
      toast.error(
        "Ocorreu um erro em uma ou mais requisições, tente mais tarde!",
        { autoClose: 5000 }
      );
    }
  }, [hasAnyError]);

  return (
    <div>
      {cardsOfCallCenterIndicators.card1 && (
        <div>{renderIndicators(dataIndicators)}</div>
      )}
      <div>{renderComparations()}</div>
    </div>
  );
}
